import { addDays, format, startOfDay, startOfWeek } from "date-fns";

export function shouldShowWeeklyDeadlineSuggestions(currentUserProfile, now = new Date()) {
  const pref = currentUserProfile?.notificationPreferences?.suggestWeeklyDeadlineEmail;
  if (pref === false) return false;
  const day = now.getDay();
  return day === 0 || day === 1 || day === 2;
}

export function getWeeklyDeadlineWeekKey(now = new Date()) {
  const weekStart = startOfWeek(now, { weekStartsOn: 0 });
  return format(weekStart, "yyyy-MM-dd");
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  if (value._seconds) return new Date(value._seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function buildWeeklyDeadlineSuggestions({
  transActive = [],
  tasksAllWithDateUpcoming = [],
  now = new Date(),
}) {
  const underContract = transActive.filter(
    (trans) => trans.status === "Under Contract"
  );
  if (underContract.length === 0) return [];

  const start = startOfDay(now);
  const end = addDays(start, 7);
  const weekKey = getWeeklyDeadlineWeekKey(now);

  return underContract
    .map((trans) => {
      const dismissedWeeks = trans.dismissedWeeklyDeadlineWeeks || [];
      if (dismissedWeeks.includes(weekKey)) {
        return null;
      }
      const deadlines = tasksAllWithDateUpcoming
        .filter((task) => {
          if (task.transactionId !== trans.id) return false;
          if (task.archived === true) return false;
          if (task.status === "Complete") return false;
          if (task.category && task.category !== "Deadline") return false;
          const taskEnd = toDate(task.end);
          if (!taskEnd) return false;
          return taskEnd >= start && taskEnd <= end;
        })
        .sort((a, b) => toDate(a.end) - toDate(b.end));

      if (deadlines.length === 0) return null;

      return {
        id: `weeklyDeadlineEmail-${trans.id}`,
        type: "weeklyDeadlineEmail",
        transactionId: trans.id,
        transactionAddress:
          trans.address?.street || trans.title || "Transaction",
        agentRepresents: trans.agentRepresents,
        client: trans.client,
        clientSecondary: trans.clientSecondary,
        clientThird: trans.clientThird,
        deadlines: deadlines.map((d) => ({
          id: d.id,
          title: d.title,
          end: toDate(d.end),
        })),
      };
    })
    .filter(Boolean);
}

function clientFirstNames(suggestion) {
  const names = [
    suggestion.client?.firstName,
    suggestion.clientSecondary?.firstName,
    suggestion.clientThird?.firstName,
  ].filter(Boolean);
  if (names.length === 0) return "there";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

function clientToRoles(suggestion) {
  if (suggestion.agentRepresents === "Buyer") {
    const roles = [];
    if (suggestion.client?.email) roles.push("Buyer");
    if (suggestion.clientSecondary?.email) roles.push("Buyer 2");
    if (suggestion.clientThird?.email) roles.push("Buyer 3");
    return roles;
  }
  if (suggestion.agentRepresents === "Seller") {
    const roles = [];
    if (suggestion.client?.email) roles.push("Seller");
    if (suggestion.clientSecondary?.email) roles.push("Seller 2");
    if (suggestion.clientThird?.email) roles.push("Seller 3");
    return roles;
  }
  return [];
}

export function buildWeeklyDeadlineEmail(suggestion, currentUserProfile) {
  const greetingName = clientFirstNames(suggestion);
  const addressLine = suggestion.transactionAddress
    ? ` for ${suggestion.transactionAddress}`
    : "";
  const deadlineLines = suggestion.deadlines
    .map((d) => {
      const dateStr = d.end ? format(d.end, "EEEE, MMM do") : "";
      return `- ${d.title || "Deadline"}${dateStr ? `: ${dateStr}` : ""}`;
    })
    .join("\n");

  const signature = currentUserProfile?.emailSigField || "";

  const subject = `Upcoming Deadlines This Week${
    suggestion.transactionAddress ? ` - ${suggestion.transactionAddress}` : ""
  }`;

  const message =
    `Hi ${greetingName},\n\n` +
    `Here are the upcoming deadlines for this week${addressLine}:\n\n` +
    `${deadlineLines}\n\n` +
    `Please let me know if you have any questions.\n` +
    `${signature}`;

  const from =
    `${currentUserProfile?.firstName || ""} ${
      currentUserProfile?.lastName || ""
    }`.trim();

  return {
    subject,
    message,
    from,
    to: clientToRoles(suggestion),
    cc: [],
    bcc: [],
    otherEmailsTo: "",
    otherEmailsCc: "",
    otherEmailsBcc: "",
  };
}
